import { createTheme } from '@mui/material/styles'
import shadows from './shadows'
import { cssBaselineStyleOverrides } from './cssBaseline'
import { CARD_BORDER, CARD_SHADOW } from './skinSx.js'

const sharedPalette = {
  primary: {
    main: '#368CFF',
  },
  grey: {
    300: '#fefefe',
    A100: '#f8f9ff',
  },
  secondary: {
    main: '#4191ff',
  },
  error: {
    main: '#f83245',
  },
  success: {
    main: '#1bc943',
  },
  info: {
    main: '#11c5db',
  },
  warning: {
    main: '#f4772e',
  },
  helpers: {
    primary: '#3c44b1',
    main: 'rgba(25, 46, 91, .035)',
  },
  contrastThreshold: 3,
  tonalOffset: 0.1,
}

const bamburghMuiButtonBase = {
  minWidth: 0,
  outline: 'none',
  transition: 'all 0.2s ease-in-out',
  '&:active': {
    boxShadow: 'none !important',
  },
  '&.Mui-disabled': {
    opacity: 0.65,
  },
  '&.card.card-box > .MuiTouchRipple-root': {
    opacity: 0.3,
  },
  '&:hover .btn-icon-transition': {
    transform: 'scale(1.1)',
  },
  '&:not(.MuiListItem-button):not(.MuiTab-root):not(:disabled):not(.MuiCheckbox-root):not(.MuiSwitch-switchBase):hover':
    {
      transform: 'translateY(-2px)',
    },
  '&.btn-transition-none:hover': {
    transform: 'translateY(0px) !important',
  },
}

const bamburghMuiButtonRoot = {
  textTransform: 'none',
  fontWeight: 'normal',
  padding: '10px 22px',
  fontSize: 14,
  lineHeight: 'initial',
  '&.Mui-disabled': {
    opacity: 1,
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
  '&.MuiButton-contained.Mui-disabled': {
    color: 'rgba(0, 0, 0, 0.5)',
    backgroundColor: '#d7d8e4',
    '& .MuiTypography-root': {
      color: 'inherit',
    },
  },
  '&.MuiButton-text.Mui-disabled, &.MuiButton-outlined.Mui-disabled': {
    color: 'rgba(0, 0, 0, 0.38)',
    backgroundColor: 'transparent',
  },
}

const bamburghMuiChip = {
  defaultProps: {
    color: 'primary',
  },
  styleOverrides: {
    deleteIcon: {
      color: 'rgba(255, 255, 255, 0.85)',
      '&:hover': { color: '#fff' },
    },
  },
}

const bamburghMuiAutocomplete = {
  defaultProps: {
    slotProps: {
      chip: { color: 'primary' },
    },
  },
}

const bamburghMuiCard = {
  styleOverrides: {
    root: {
      borderRadius: '0.75rem',
      transition: 'all 0.2s ease-in-out',
      '& .card-title': {
        fontSize: '1.15rem',
      },
      '& .MuiCardContent-root': {
        padding: '1.25rem',
      },
    },
  },
  variants: [
    {
      props: { variant: 'outlined' },
      style: {
        border: CARD_BORDER,
        boxShadow: CARD_SHADOW,
        overflow: 'visible',
        '& .card-header': {
          display: 'flex',
          alignItems: 'center',
        },
        '& .card-header--title': {
          flexGrow: 1,
          minWidth: 0,
          '& > b': { display: 'block' },
          '& > small': {
            textTransform: 'uppercase',
            display: 'block',
            opacity: 0.3,
            fontWeight: 'bold',
          },
        },
      },
    },
  ],
}

function sharedComponents(isDark) {
  return {
    MuiButtonBase: {
      styleOverrides: {
        root: bamburghMuiButtonBase,
      },
    },
    MuiButton: {
      styleOverrides: {
        root: bamburghMuiButtonRoot,
        sizeSmall: {
          padding: '6px 20px',
          fontSize: 14,
        },
        sizeLarge: {
          padding: '16px 28px',
          fontSize: 16,
        },
        outlined: {
          padding: '10px 22px',
          fontSize: 14,
        },
        text: {
          padding: '10px 22px',
          fontSize: 14,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#070919',
          padding: '8px 16px',
          fontSize: '13px',
        },
        arrow: {
          color: '#070919',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: '4px',
          boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.2)',
          width: 'max-content',
        },
        root: {},
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: '8px 0',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxSizing: 'border-box',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '0.65rem',
          boxShadow: '0 1rem 3rem rgba(0, 0, 0, 0.575)',
          '& .MuiDialogTitle-root': {
            borderBottom: '1px solid #e6e7f1',
          },
          '& .MuiDialogActions-root': {
            borderTop: '1px solid #e6e7f1',
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 58,
          height: 38,
          padding: 0,
          overflow: 'visible',
          borderRadius: 50,
          '& .MuiSwitch-switchBase': {
            padding: 9,
            color: '#fff',
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                opacity: 1,
                backgroundColor: '#368cff',
              },
            },
          },
          '& .MuiSwitch-thumb': {
            width: 20,
            height: 20,
            boxShadow:
              '0 2px 1px -1px rgba(0,0,0,.2), 0 1px 1px 0 rgba(0,0,0,.14), 0 1px 3px 0 rgba(0,0,0,.12)',
          },
          '& .MuiSwitch-track': {
            borderRadius: 50,
            opacity: 1,
            backgroundColor: '#dfe0ea',
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        rail: {
          height: 4,
          borderRadius: '0.65rem',
          background: '#dfe0ea',
          opacity: 1,
        },
        track: {
          height: 4,
          borderRadius: '0.65rem',
        },
        thumb: {
          width: 16,
          height: 16,
          border: '2px solid transparent',
          background: '#fff',
        },
      },
    },
    MuiCard: bamburghMuiCard,
    MuiChip: bamburghMuiChip,
    MuiAutocomplete: bamburghMuiAutocomplete,
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingTop: '8px',
          paddingBottom: '8px',
        },
        button: {
          '&:hover': {
            backgroundColor: isDark
              ? 'rgba(255, 255, 255, 0.08)'
              : 'rgba(0, 0, 0, 0.04)',
          },
        },
      },
    },
  }
}

/**
 * @param {'light' | 'dark'} mode
 * @param {{ sm?: number, lg?: number }} breakpoints
 */
export function createMuiThemeFromBreakpoints(
  mode = 'light',
  { sm = 576, lg = 1300 } = {},
) {
  const isDark = mode === 'dark'
  return createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      ...sharedPalette,
      ...(isDark
        ? {
            background: {
              default: '#121212',
              paper: '#1e1e1e',
            },
            text: {
              primary: 'rgba(255, 255, 255, 0.87)',
              secondary: 'rgba(255, 255, 255, 0.6)',
            },
            helpers: {
              primary: '#8b93e8',
              main: 'rgba(255, 255, 255, 0.06)',
            },
          }
        : {}),
    },
    breakpoints: {
      values: {
        xs: 0,
        sm,
        md: 768,
        lg,
        xl: 2000,
      },
    },
    shape: {
      borderRadius: '0.2rem',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: cssBaselineStyleOverrides(),
      },
      ...sharedComponents(isDark),
    },
    typography: {
      fontFamily: ['Heebo', 'sans-serif'].join(','),
      fontSize: 14,
    },
    shadows,
  })
}

/** App shell theme (marketing/public + logged-in). */
export function createMuiTheme(mode = 'light') {
  return createMuiThemeFromBreakpoints(mode, { sm: 576, lg: 1300 })
}

/** Property-dashboard / public-property breakpoint variant (sm/lg only). */
export function createMuiTheme2(mode = 'light') {
  return createMuiThemeFromBreakpoints(mode, { sm: 600, lg: 1500 })
}

const MuiTheme = createMuiTheme('light')

export default MuiTheme
